/* eslint-disable @typescript-eslint/no-explicit-any */
import { useGetUploadUrlMutation } from '@/common/utils/graphql.generated';
import { useFacility } from '@/contexts/facility-context/hook';
import { useCurrentUser } from '@/hooks';
import { useSet } from '@mantine/hooks';
import React, { createContext, ReactNode, useCallback, useMemo, useState } from 'react';
import { AssetStatus } from '../../../../../../backend/src/types.generated';
import { BAG_AUDIT_ERRORS } from './constants';
import {
	useFacilityBagAuditMutation,
	useSearchBagsForAuditLazyQuery,
	useUploadBagAuditPhotoMutation,
} from './graphql.generated';
import { BagAuditPhotoService, BagAuditService } from './services';
import type {
	BagAuditContextValue,
	BagAuditError,
	BagAuditObject,
	BagAuditPageType,
} from './types';

// Create the context with undefined as default
// eslint-disable-next-line react-refresh/only-export-components
export const BagAuditContext = createContext<BagAuditContextValue | undefined>(undefined);

// Provider component props
interface BagAuditContextProviderProps {
	children: ReactNode;
}

// Provider component
export const BagAuditContextProvider: React.FC<BagAuditContextProviderProps> = ({ children }) => {
	const [page, setPage] = useState<BagAuditPageType>('SCAN');
	const [loadingSubmission, setLoadingSubmission] = useState(false);
	const [scanError, setScanError] = useState<BagAuditError | undefined>();
	const [evaluatingBag, setEvaluatingBag] = useState<BagAuditObject | null>(null);
	const [showManualCodeEntry, setShowManualCodeEntry] = useState(false);

	const { currentFacility } = useFacility();
	const { currentUser } = useCurrentUser();
	const ignoredCodes = useSet<string>();

	// GraphQL hooks
	const [getTrackableAssets] = useSearchBagsForAuditLazyQuery();
	const [facilityBagAuditMutation] = useFacilityBagAuditMutation();
	const [addBagAuditPhotoMutation] = useUploadBagAuditPhotoMutation();
	const [getUploadUrlMutation] = useGetUploadUrlMutation();

	// Create service instances
	const photoService = useMemo(
		() =>
			new BagAuditPhotoService(
				(variables) => getUploadUrlMutation({ variables }) as any,
				(variables) => addBagAuditPhotoMutation(variables) as any
			),
		[getUploadUrlMutation, addBagAuditPhotoMutation]
	);

	const auditService = useMemo(
		() => new BagAuditService(facilityBagAuditMutation as any, photoService),
		[facilityBagAuditMutation, photoService]
	);

	const handleScan = useCallback(
		async (code: string) => {
			try {
				setScanError(undefined);

				if (ignoredCodes.has(code)) {
					return;
				}
				ignoredCodes.add(code);

				const data = await getTrackableAssets({
					variables: { filter: { scannableCode: code.split('/').pop() } },
				}).then((res) => {
					if (
						!res.data?.trackableAssets?.[0]?.status ||
						![AssetStatus.BagAudit, AssetStatus.BagScanned, AssetStatus.BagContaminated].includes(
							res.data?.trackableAssets?.[0]?.status as AssetStatus
						)
					) {
						setScanError(BAG_AUDIT_ERRORS.INVALID_BAG_STATE);
						return null;
					} else if (res.data?.trackableAssets?.[0]) {
						return res.data.trackableAssets[0];
					}
					return null;
				});

				if (
					data &&
					data.assetType &&
					data.intendedMaterial &&
					(data.status === null || data.status === AssetStatus.BagAudit)
				) {
					setEvaluatingBag({
						id: data.id,
						scannableCode: code,
						assetType: data.assetType,
						materialType: data.intendedMaterial,
						photos: [],
						weight: undefined,
						isContaminated: false,
						isDamaged: false,
						contamination: 0,
						contaminationDetails: '',
					});
					setPage('FORM');
				} else {
					setScanError(BAG_AUDIT_ERRORS.INVALID_BAG_STATE);
				}
			} catch {
				setScanError(BAG_AUDIT_ERRORS.INVALID_BAG_STATE);
			}
		},
		[ignoredCodes, getTrackableAssets, setScanError]
	);

	const handleSubmit = useCallback(async () => {
		if (!evaluatingBag || !currentFacility?.id || !currentUser?.id) {
			return { success: false, error: BAG_AUDIT_ERRORS.MISSING_REQUIRED_DATA };
		}

		setLoadingSubmission(true);

		try {
			const result = await auditService.submitBagAudit(
				evaluatingBag,
				currentFacility.id,
				currentUser.id
			);

			if (result.success) {
				// Reset state and navigate back to scan page
				setEvaluatingBag(null);
				setPage('SCAN');
			}

			return result;
		} catch {
			return { success: false, error: BAG_AUDIT_ERRORS.AUDIT_CREATION_FAILED };
		} finally {
			setLoadingSubmission(false);
		}
	}, [evaluatingBag, currentFacility?.id, currentUser?.id, auditService]);

	const contextValue: BagAuditContextValue = useMemo(
		() => ({
			page,
			setPage,
			handleScan,
			handleSubmit,
			showManualCodeEntry,
			setShowManualCodeEntry,
			evaluatingBag,
			setEvaluatingBag,
			loadingSubmission,
			scanError,
			setScanError,
		}),
		[
			page,
			handleScan,
			handleSubmit,
			showManualCodeEntry,
			evaluatingBag,
			loadingSubmission,
			scanError,
		]
	);

	return <BagAuditContext.Provider value={contextValue}>{children}</BagAuditContext.Provider>;
};
